import { Jwt } from 'jsonwebtoken';
import * as JwtDecoder from 'jwt-decode';
import { DateTime } from 'luxon';
import { AuthToken } from '../auth.model';
import { GatewayJwt, GatewayJwtEncoded, ShopJwt, ShopJwtEncoded } from './apps';
import { AppJwt, AppJwtEncoded } from './jwt.model';

export class JwtHelper {
  private static readonly instance: JwtHelper = new JwtHelper();

  public static decodeJwt(data: AppJwtEncoded): AuthToken {
    return JwtHelper.instance.decodeJwt(data);
  }

  public static parseJwt(token: string, decoded?: Jwt): AuthToken {
    return JwtHelper.instance.parseJwt(token, decoded);
  }

  public static isGatewayJwtEncoded(jwt: AppJwtEncoded): jwt is GatewayJwtEncoded {
    return (jwt as GatewayJwtEncoded).token !== undefined;
  }

  public static isShopJwtEncoded(jwt: AppJwtEncoded): jwt is ShopJwtEncoded {
    return (jwt as ShopJwtEncoded).id_token !== undefined;
  }

  public static isGatewayJwt(jwt: unknown): jwt is GatewayJwt {
    return (jwt as GatewayJwt).payload?.userId !== undefined;
  }

  public static isShopJwt(jwt: unknown): jwt is ShopJwt {
    return (jwt as ShopJwt).payload?.sub !== undefined;
  }

  private decodeJwt(data: AppJwtEncoded): AuthToken {
    if (JwtHelper.isGatewayJwtEncoded(data)) {
      return this.parseJwt(data.token);
    }

    if (JwtHelper.isShopJwtEncoded(data)) {
      return this.parseJwt(data.id_token);
    }

    this.errorUnknownTokenType();
  }

  private parseJwt(token: string, decoded?: Jwt): AuthToken {
    const jwt: Jwt = decoded ?? this.decodeToken(token);
    
    if (JwtHelper.isGatewayJwt(jwt)) {
      return {
        bearer: token,
        userId: jwt.payload.userId,
        expires: jwt.payload.expires,
      };
    }

    if (JwtHelper.isShopJwt(jwt)) {
      return {
        bearer: token,
        userId: jwt.payload.sub,
        expires: DateTime.fromMillis(jwt.payload.exp * 1000).toISO() ?? 0,
        roles: jwt.payload.roles,
        audience: jwt.payload.aud,
      };
    }

    this.errorUnknownTokenType();
  }

  private decodeToken(token: string): AppJwt {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return { payload: JwtDecoder.jwtDecode(token) } as AppJwt;
  }

  private errorUnknownTokenType(): never {
    throw new Error('Unknown token type.');
  }
}
